import React, { useState } from 'react';
import { Menu, Box, Typography, FormGroup, FormControlLabel, Checkbox, Divider, RadioGroup, Radio } from '@mui/material';
import * as Icons from '@mui/icons-material';
import { Button, IconButton, Tooltip, Select, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase.js';
import { useUser } from './contexts/UserContext.js';

function RecipeFilters({
  statusFilters,
  setStatusFilters,
  originFilters,
  setOriginFilters,
  uniqueOrigins,
  originFilter,
  activeSetId,
  setActiveSetId,
  activeSet,
  setActiveSet,
  onSetChange,
  sortOrder,
  setSortOrder,
}) {
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [ingredientSets, setIngredientSets] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useUser();

  // Load ingredient sets when filter menu opens
  const loadIngredientSets = async () => {
    if (!user) return;
    
    try {
      const setsRef = collection(db, `users/${user.uid}/ingredientSets`);
      const snapshot = await getDocs(setsRef);
      const sets = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setIngredientSets(sets);
      
      // Get active set from localStorage if not already set
      if (!activeSetId) {
        const savedActiveSetId = localStorage.getItem('activeIngredientSet');
        if (savedActiveSetId && sets.some(set => set.id === savedActiveSetId)) {
          setActiveSetId(savedActiveSetId);
          const foundSet = sets.find(set => set.id === savedActiveSetId);
          setActiveSet(foundSet);
          
          // Call onSetChange with the active set
          if (onSetChange) {
            onSetChange(foundSet);
          }
        }
      }
    } catch (error) {
      console.error('Error loading ingredient sets:', error);
    }
  };

  const handleFilterMenuOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterMenuOpen(true);
    loadIngredientSets(); // Load ingredient sets when menu opens
  };

  const handleFilterMenuClose = () => {
    setFilterMenuOpen(false);
    setFilterAnchorEl(null);
  };

  // Handle ingredient set selection
  const handleSetChange = (event) => {
    const setId = event.target.value;
    setActiveSetId(setId);
    
    if (setId) {
      const selectedSet = ingredientSets.find(set => set.id === setId);
      if (selectedSet) {
        localStorage.setItem('activeIngredientSet', setId);
        setActiveSet(selectedSet);
        
        // Call onSetChange with the selected set
        if (onSetChange) {
          onSetChange(selectedSet);
        }
      }
    } else {
      // If no set is selected, remove from localStorage and call onSetChange with null
      localStorage.removeItem('activeIngredientSet');
      setActiveSet(null);
      
      if (onSetChange) {
        onSetChange(null);
      }
    }
  };

  // Handle sort order change
  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
    localStorage.setItem('recipeSortOrder', event.target.value);
  };

  return (
    <>
      {isMobile ? (
        <Tooltip title="Filter & Sort">
          <IconButton onClick={handleFilterMenuOpen} sx={{ backgroundColor: theme.palette.action.hover }}>
            <Icons.Tune />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          startIcon={<Icons.Tune />}
          onClick={handleFilterMenuOpen}
          sx={{ backgroundColor: theme.palette.action.hover, textTransform: 'none' }}
        >
          Filter & Sort
        </Button>
      )}
      <Menu
        anchorEl={filterAnchorEl}
        open={filterMenuOpen}
        onClose={handleFilterMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: { xs: 300, sm: 320 }, maxHeight: { xs: '80vh', sm: '70vh' }, overflow: 'auto', mt: 0.5 },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          {/* Sort Order - New section at the top */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Sort Order
          </Typography>
          <RadioGroup
            value={sortOrder}
            onChange={handleSortChange}
            sx={{ mb: 2 }}
          >
            <FormControlLabel 
              value="asc" 
              control={<Radio size="small" />} 
              label="A to Z" 
            />
            <FormControlLabel 
              value="desc" 
              control={<Radio size="small" />} 
              label="Z to A" 
            />
          </RadioGroup>

          <Divider sx={{ my: 1 }} />

          {/* Ingredient Set Selection */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Ingredient Set
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Select
              fullWidth
              size="small"
              value={activeSetId || ""}
              onChange={handleSetChange}
              displayEmpty
              sx={{ mb: 1 }}
            >
              <MenuItem value="">📋 Main Inventory</MenuItem>
              {ingredientSets.map(set => (
                <MenuItem key={set.id} value={set.id}>🍹 {set.name}</MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary">
              Select an ingredient set to filter recipes based on available ingredients
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Status Filters
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.includes('favorites')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setStatusFilters([...statusFilters, 'favorites']);
                    } else {
                      setStatusFilters(statusFilters.filter(f => f !== 'favorites'));
                    }
                  }}
                />
              }
              label="Favorites"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.includes('bookmarks')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setStatusFilters([...statusFilters, 'bookmarks']);
                    } else {
                      setStatusFilters(statusFilters.filter(f => f !== 'bookmarks'));
                    }
                  }}
                />
              }
              label="Bookmarks"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.includes('can-make')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setStatusFilters([...statusFilters, 'can-make']);
                    } else {
                      setStatusFilters(statusFilters.filter(f => f !== 'can-make'));
                    }
                  }}
                />
              }
              label="Can Make"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.includes('missing-1')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setStatusFilters([...statusFilters, 'missing-1']);
                    } else {
                      setStatusFilters(statusFilters.filter(f => f !== 'missing-1'));
                    }
                  }}
                />
              }
              label="Missing 1 Ingredient"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.includes('missing-2')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setStatusFilters([...statusFilters, 'missing-2']);
                    } else {
                      setStatusFilters(statusFilters.filter(f => f !== 'missing-2'));
                    }
                  }}
                />
              }
              label="Missing 2 Ingredients"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.includes('missing-3')}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setStatusFilters([...statusFilters, 'missing-3']);
                    } else {
                      setStatusFilters(statusFilters.filter(f => f !== 'missing-3'));
                    }
                  }}
                />
              }
              label="Missing 3 Ingredients"
            />
          </FormGroup>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Origin Filters
          </Typography>
          <FormGroup>
            {uniqueOrigins.map(origin => (
              <FormControlLabel
                key={origin}
                control={
                  <Checkbox
                    checked={originFilters.includes(origin)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (checked && !originFilter) {
                        setOriginFilters([...originFilters, origin]);
                      } else if (!checked) {
                        setOriginFilters(originFilters.filter(o => o !== origin));
                      }
                    }}
                    disabled={!!originFilter}
                  />
                }
                label={origin}
              />
            ))}
          </FormGroup>
        </Box>
      </Menu>
    </>
  );
}

export default RecipeFilters;